// ============================================
// PRTCL (formerly PenRx) Shared UI Components
// Premium button, card, input, badge, modal
// ============================================

import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Components.css';

/* ---- Button ---- */
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  icon,
  className = '',
  disabled,
  ...props
}) => (
  <button
    className={`btn btn-${variant} btn-${size} ${fullWidth ? 'btn-full' : ''} ${loading ? 'btn-loading' : ''} ${className}`}
    disabled={disabled || loading}
    {...props}
  >
    {loading && <span className="btn-spinner" />}
    {icon && !loading && <span className="btn-icon">{icon}</span>}
    {children}
  </button>
);

/* ---- Card ---- */
interface CardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  glass?: boolean;
  glow?: boolean;
  padding?: 'sm' | 'md' | 'lg';
  style?: React.CSSProperties;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  onClick,
  glass = false,
  glow = false,
  padding = 'md',
  style,
}) => (
  <div
    className={`card card-pad-${padding} ${glass ? 'card-glass' : ''} ${glow ? 'card-glow' : ''} ${onClick ? 'card-interactive' : ''} ${className}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    style={style}
  >
    {children}
  </div>
);

/* ---- Input ---- */
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Optional node rendered on the right side of the label row (e.g. a "Forgot password?" link). */
  labelAction?: React.ReactNode;
  description?: string;
  helper?: string;
  error?: string;
  suffix?: React.ReactNode;
  icon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  labelAction,
  description,
  helper,
  error,
  suffix,
  icon,
  className = '',
  id,
  type,
  onWheel,
  ...props
}) => {
  // Stable unique id via useId: the old label-slug fallback produced
  // "input-undefined" for label-less fields and DUPLICATE ids when two
  // fields shared a label (e.g. multiple "Dose" inputs in the wizard),
  // which breaks the label htmlFor association for assistive tech.
  const reactId = useId();
  const inputId = id || `input-${reactId}`;
  // Prevent scroll wheel from changing number input values
  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === 'number') {
      (e.target as HTMLInputElement).blur();
    }
    onWheel?.(e);
  };
  return (
    <div className={`input-group ${error ? 'input-error' : ''} ${className}`}>
      {label && (
        labelAction ? (
          <div className="input-label-row">
            <label htmlFor={inputId} className="input-label">{label}</label>
            <span className="input-label-action">{labelAction}</span>
          </div>
        ) : (
          <label htmlFor={inputId} className="input-label">{label}</label>
        )
      )}
      {description && <span className="input-description">{description}</span>}
      <div className="input-wrapper">
        {icon && <span className="input-icon">{icon}</span>}
        <input id={inputId} className="input-field" type={type} onWheel={handleWheel} {...props} />
        {suffix && <span className="input-suffix">{suffix}</span>}
      </div>
      {error && <span className="input-error-text">{error}</span>}
      {helper && !error && <span className="input-helper">{helper}</span>}
    </div>
  );
};

/* ---- Badge ---- */
interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'accent';
  size?: 'sm' | 'md';
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'sm',
  className = '',
}) => (
  <span className={`badge badge-${variant} badge-${size} ${className}`}>
    {children}
  </span>
);

/* ---- Modal (Bottom Sheet) ---- */
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Force toolbar header with X / Title even without a Done action */
  toolbar?: boolean;
  /** Show toolbar header with X / Title / Done instead of drag handle + title */
  onDone?: () => void;
  doneLabel?: string;
  doneDisabled?: boolean;
  doneLoading?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  toolbar,
  onDone,
  doneLabel = 'Done',
  doneDisabled,
  doneLoading,
}) => {
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Focus management (deps: isOpen ONLY, so re-renders can't clobber the
  // restore target): on open, remember the trigger and move focus into the
  // sheet; on close/unmount, hand focus back to the trigger.
  useEffect(() => {
    if (!isOpen) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    // Don't steal focus from a child that autoFocus-ed itself (child
    // autoFocus applies at DOM insertion, before this effect runs).
    const sheet = sheetRef.current;
    if (sheet && !sheet.contains(document.activeElement)) sheet.focus();
    return () => {
      restoreFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  // Keyboard: Escape closes (standard dialog behavior); Tab wraps within
  // the sheet so keyboard focus can't wander into the page behind the
  // overlay.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const focusables = sheet.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === sheet)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const hasToolbar = toolbar || !!onDone;
  // Portal to document.body so the modal escapes ancestor stacking
  // contexts. Specifically, `.page-enter` uses an animation that leaves
  // a residual `transform` applied, which creates a stacking context
  // and traps `z-index: var(--z-modal)` below sibling fixed elements
  // like the tab bar. Portal fixes this by rendering directly under
  // <body>.
  return createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      // aria-labelledby points at the rendered title; aria-label={title}
      // used to leave the dialog with an UNDEFINED accessible name when no
      // title was passed.
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : 'Dialog'}
    >
      <div className="modal-sheet animate-slide-up" ref={sheetRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        {hasToolbar ? (
          <div className="modal-toolbar">
            <button className="modal-toolbar-close" onClick={onClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            {title && <h3 id={titleId} className="modal-toolbar-title">{title}</h3>}
            {onDone ? (
              <button
                className={`modal-toolbar-done ${doneDisabled ? 'modal-toolbar-done-disabled' : ''}`}
                onClick={onDone}
                disabled={doneDisabled || doneLoading}
              >
                {doneLoading ? 'Saving...' : doneLabel}
              </button>
            ) : (
              <div style={{ width: 36 }} />
            )}
          </div>
        ) : (
          <>
            <div className="modal-handle" />
            {title && <h3 id={titleId} className="modal-title">{title}</h3>}
          </>
        )}
        <div className="modal-content">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

/* ---- Empty State ---- */
interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
}) => (
  <div className="empty-state">
    {icon && <div className="empty-state-icon">{icon}</div>}
    <h3 className="empty-state-title">{title}</h3>
    {description && <p className="empty-state-desc">{description}</p>}
    {action && <div className="empty-state-action">{action}</div>}
  </div>
);

/* ---- Progress Bar ---- */
interface ProgressBarProps {
  value: number;
  max?: number;
  size?: 'sm' | 'md';
  label?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  size = 'md',
  label,
}) => {
  const percent = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={`progress-bar progress-bar-${size}`} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max} aria-label={label}>
      <div className="progress-bar-fill" style={{ width: `${percent}%` }} />
    </div>
  );
};

/* ---- Select ---- */
interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  label?: string;
  /** Beginner-level explanatory text rendered between label and field. */
  description?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  helper?: string;
  /** Validation error text rendered below the field; preempts helper. */
  error?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  description,
  options,
  value,
  onChange,
  helper,
  error,
}) => {
  // useId for the same duplicate/undefined-id reasons as Input above.
  const selectId = `select-${useId()}`;
  return (
    <div className={`input-group ${error ? 'input-error' : ''}`}>
      {label && <label htmlFor={selectId} className="input-label">{label}</label>}
      {description && <span className="input-description">{description}</span>}
      <div className="input-wrapper">
        <select
          id={selectId}
          className="input-field select-field"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span className="select-arrow">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>
      {error && <span className="input-error-text">{error}</span>}
      {helper && !error && <span className="input-helper">{helper}</span>}
    </div>
  );
};

/* ---- Back Button ----
   Single unified back-button used across every page that needs one
   (Protocol Detail, Edit Protocol, Library Detail, History, Analyze,
   Legal). Modern minimal "icon-in-soft-chip" pattern: 36x36 rounded
   square with bg-tertiary background, chevron-only, matches the soft
   segmented-control language used everywhere else in the app.

   Variant 'overlay' is for use over dark hero cards or photos where
   the default chip would disappear - swaps to a translucent white
   chip + dark icon for contrast.
*/
interface BackButtonProps {
  onClick: () => void;
  ariaLabel?: string;
  variant?: 'default' | 'overlay';
  className?: string;
}

export const BackButton: React.FC<BackButtonProps> = ({
  onClick,
  ariaLabel = 'Go back',
  variant = 'default',
  className = '',
}) => (
  <button
    type="button"
    className={`back-btn back-btn--${variant} ${className}`.trim()}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="10 3 5 8 10 13" />
    </svg>
  </button>
);

/* ---- Dosing Disclaimer Banner ---- */
interface DosingDisclaimerProps {
  variant?: 'inline' | 'banner' | 'calculator';
  className?: string;
}

export const DosingDisclaimer: React.FC<DosingDisclaimerProps> = ({
  variant = 'inline',
  className = '',
}) => (
  <div className={`dosing-disclaimer dosing-disclaimer-${variant === 'calculator' ? 'inline' : variant} ${className}`}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="dosing-disclaimer-icon">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <span className="dosing-disclaimer-text">
      {variant === 'banner' ? (
        <>The protocols and dosing information shown here are sourced from published research and professional community discussion. This is <strong>not medical advice</strong> and should not be followed as a recommendation. Always consult your doctor before starting or changing any protocol.</>
      ) : variant === 'calculator' ? (
        <>Consult your doctor for your recommended dose. Protocol dosing found in the peptide library is sourced from research studies and professional discussion - <strong>not recommendations to follow</strong>.</>
      ) : (
        <>Dosing discussed in research and by industry professionals - <strong>not medical advice</strong>. Consult your doctor.</>
      )}
    </span>
  </div>
);
